import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../services/supabase';
import { useLovense } from '../../hooks/useLovense';
import { SafeWordButton } from '../SafeWordButton';

type Phase = 'inhale' | 'hold' | 'exhale' | 'rest';

export const Breathing = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const role = (searchParams.get('role') || 'A') as 'A' | 'B';
  
  const { vibrate, stop } = useLovense();
  
  const [phase, setPhase] = useState<Phase>('rest');
  const [progress, setProgress] = useState(0);
  const [cycles, setCycles] = useState(0);
  const [isActive, setIsActive] = useState(false);
  
  const phaseRef = useRef<Phase>('rest');
  const progressRef = useRef(0);
  const channelRef = useRef<any>(null);

  const PHASE_DURATION = {
    inhale: 4000,
    hold: 4000,
    exhale: 6000,
    rest: 2000
  };

  useEffect(() => {
    if (!isActive) return;

    const runCycle = () => {
      // Inhale
      setPhase('inhale');
      phaseRef.current = 'inhale';
      vibrate(5);
      animateProgress(PHASE_DURATION.inhale, () => {
        if (!isActive) return;
        
        // Hold
        setPhase('hold');
        phaseRef.current = 'hold';
        vibrate(10);
        animateProgress(PHASE_DURATION.hold, () => {
          if (!isActive) return;
          
          // Exhale
          setPhase('exhale');
          phaseRef.current = 'exhale';
          vibrate(3);
          animateProgress(PHASE_DURATION.exhale, () => {
            if (!isActive) return;
            
            // Rest
            setPhase('rest');
            phaseRef.current = 'rest';
            stop();
            animateProgress(PHASE_DURATION.rest, () => {
              if (isActive) {
                setCycles(c => c + 1);
                runCycle();
              }
            });
          });
        });
      });
    };

    const animateProgress = (duration: number, onComplete: () => void) => {
      const start = Date.now();
      const animate = () => {
        const elapsed = Date.now() - start;
        const p = Math.min(elapsed / duration, 1);
        setProgress(p);
        progressRef.current = p;
        
        if (p < 1 && isActive) {
          requestAnimationFrame(animate);
        } else {
          onComplete();
        }
      };
      animate();
    };

    runCycle();

    return () => {
      stop();
    };
  }, [isActive, vibrate, stop]);

  useEffect(() => {
    const channel = supabase.channel(`game-${sessionId}`);
    channelRef.current = channel;
    
    channel.subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  const toggleBreathing = useCallback(() => {
    setIsActive(!isActive);
    if (!isActive) {
      channelRef.current?.send({
        type: 'broadcast',
        event: 'game_action',
        payload: { action: { playerId: role, action: 'breath_in' }, response: {} }
      });
    } else {
      stop();
    }
  }, [isActive, sessionId, role, stop]);

  const getPhaseText = () => {
    switch (phase) {
      case 'inhale': return 'Breathe In';
      case 'hold': return 'Hold';
      case 'exhale': return 'Breathe Out';
      case 'rest': return 'Rest';
    }
  };

  const getPhaseColor = () => {
    switch (phase) {
      case 'inhale': return 'var(--primary)';
      case 'hold': return 'var(--accent)';
      case 'exhale': return 'var(--secondary)';
      case 'rest': return 'var(--text-muted)';
    }
  };

  return (
    <div className="game-container">
      <div style={{ textAlign: 'center' }}>
        <h1>Breathing Sync</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Synchronize your breathing with the rhythm
        </p>
      </div>

      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', color: 'var(--primary)' }}>{cycles}</div>
        <div style={{ color: 'var(--text-muted)' }}>Cycles Completed</div>
      </div>

      <motion.div
        animate={{
          scale: phase === 'inhale' ? 1.5 : phase === 'hold' ? 1.5 : 1,
          opacity: phase === 'rest' ? 0.5 : 1
        }}
        transition={{ duration: phase === 'inhale' ? 4 : phase === 'exhale' ? 6 : 0.5 }}
        style={{
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${getPhaseColor()} 0%, transparent 70%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 60px ${getPhaseColor()}`
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
            {getPhaseText()}
          </div>
          <div style={{ fontSize: '2rem' }}>
            {phase === 'inhale' && '⬆️'}
            {phase === 'hold' && '⏸️'}
            {phase === 'exhale' && '⬇️'}
            {phase === 'rest' && '💤'}
          </div>
        </div>
      </motion.div>

      {/* Progress bar */}
      <div style={{ width: '300px', height: '8px', background: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden' }}>
        <motion.div
          style={{
            height: '100%',
            background: getPhaseColor(),
            width: `${progress * 100}%`
          }}
        />
      </div>

      <button
        className={`btn btn-lg ${isActive ? 'btn-danger' : 'btn-primary'}`}
        onClick={toggleBreathing}
      >
        {isActive ? 'Stop Session' : 'Start Breathing'}
      </button>

      <div style={{ color: 'var(--text-muted)', textAlign: 'center', maxWidth: '400px' }}>
        Follow the expanding and contracting circle. Inhale as it grows, 
        hold when it's large, and exhale as it shrinks.
      </div>

      <SafeWordButton sessionId={sessionId!} />
    </div>
  );
};